/**
 * Asynchronously downloads blobs from Azure Blob Storage and converts them to File objects.
 *
 * @param {Function} downloadBlob - A function that downloads a blob given a container name and a blob path.
 * @param {string} containerName - The name of the container in Azure Blob Storage.
 * @param {string[]} blobPaths - An array of blob paths to be downloaded.
 *
 * @returns {Promise<File[]>} A promise that resolves to an array of File objects.
 *
 * @throws Will throw an error if any of the blobs cannot be downloaded.
 *
 * This function takes a function `downloadBlob`, a container name, and an array of blob paths.
 * It attempts to download each blob using `downloadBlob`. If successful, it creates a File object
 * for each blob. If any blob cannot be downloaded (e.g., the blob is not found), it throws an error.
 * The function uses `Promise.all` to wait for all download promises to resolve, ensuring that all
 * files are downloaded (or an error is thrown) before proceeding.
 */
export const downloadBlobToFiles = async (
    downloadBlob: (
        containerName: string,
        blobPath: string
    ) => Promise<Blob | undefined>,
    containerName: string,
    blobPaths: string[]
): Promise<File[]> => {
    const filePromises = blobPaths.map(async (path) => {
        const blob = await downloadBlob(containerName, path);
        if (!blob) {
            throw new Error(`Failed to download file: ${path}`);
        }
        const fileName = path.split('/').pop() || 'unknown';
        return new File([blob], fileName);
    });

    const files = await Promise.all(filePromises);
    return files;
};
